/**
 * SupportFormPublic - Public-facing customer feedback form
 * Standalone page that can be embedded or accessed directly
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Bug, Sparkles, HelpCircle, Loader2, CheckCircle, Send,
  Mail, AlertCircle, Paperclip, X, FileText, Image as ImageIcon, Upload
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { supportFormsApi, API_BASE_URL } from '../services/api';
import { cn } from '../lib/utils';
import { MarkdownEditor } from '../components/ui/markdown-editor';

// Type configuration
const TYPE_CONFIG = {
  bug: { 
    label: 'Report a Bug', 
    icon: Bug, 
    color: 'border-red-500 bg-red-50 dark:bg-red-900/20',
    activeColor: 'border-red-500 bg-red-100 dark:bg-red-900/30 ring-2 ring-red-500',
    description: 'Something isn\'t working correctly'
  },
  feature: { 
    label: 'Request a Feature', 
    icon: Sparkles, 
    color: 'border-blue-500 bg-blue-50 dark:bg-blue-900/20',
    activeColor: 'border-blue-500 bg-blue-100 dark:bg-blue-900/30 ring-2 ring-blue-500',
    description: 'Suggest a new feature or improvement'
  },
  question: { 
    label: 'Ask a Question', 
    icon: HelpCircle, 
    color: 'border-purple-500 bg-purple-50 dark:bg-purple-900/20',
    activeColor: 'border-purple-500 bg-purple-100 dark:bg-purple-900/30 ring-2 ring-purple-500',
    description: 'Get help or ask about something'
  },
};

// Allowed file types for attachments (safe, low-risk)
const ALLOWED_FILE_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'application/pdf',
  'text/plain',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB per file
const MAX_FILES = 5;

const SupportFormPublic = () => {
  const { shareToken } = useParams();
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === 'true';
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  
  // Form state
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    email: '',
    name: '',
    company: '',
    phone: '',
  });
  const [attachments, setAttachments] = useState([]); // { file, preview, uploading, error }
  const [isDragging, setIsDragging] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  
  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [requiresVerification, setRequiresVerification] = useState(false);

  // Load form configuration
  useEffect(() => {
    const loadForm = async () => {
      try {
        setLoading(true);
        const response = await supportFormsApi.getPublicForm(shareToken);
        setForm(response);
        
        // Pre-select type if only one is enabled or from URL params
        if (response.enabled_types.length === 1) {
          setSelectedType(response.enabled_types[0]);
        } else if (response.allow_url_params) {
          const typeParam = searchParams.get('type');
          if (typeParam && response.enabled_types.includes(typeParam)) {
            setSelectedType(typeParam);
          }
        }
        
        // Pre-fill from URL params
        if (response.allow_url_params) {
          setFormData(prev => ({
            ...prev,
            title: searchParams.get('title') || prev.title,
            description: searchParams.get('description') || prev.description,
            email: searchParams.get('email') || prev.email,
            name: searchParams.get('name') || prev.name,
          }));
        }
        
        setError(null);
      } catch (err) {
        console.error('Error loading form:', err);
        setError('This form is not available');
      } finally {
        setLoading(false);
      }
    };
    
    if (shareToken) {
      loadForm();
    }
  }, [shareToken, searchParams]);

  // Send height to parent for iframe resize
  useEffect(() => {
    if (isEmbed && form) {
      const sendHeight = () => {
        const height = document.body.scrollHeight;
        window.parent.postMessage({
          type: 'sr-form-resize',
          formId: form.form_id,
          height
        }, '*');
      };
      
      sendHeight();
      window.addEventListener('resize', sendHeight);
      return () => window.removeEventListener('resize', sendHeight);
    }
  }, [isEmbed, form, submitted]);

  // File handling functions
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    processFiles(files);
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const processFiles = (files) => {
    const validFiles = [];
    
    for (const file of files) {
      // Check max files
      if (attachments.length + validFiles.length >= MAX_FILES) {
        alert(`Maximum ${MAX_FILES} files allowed`);
        break;
      }
      
      // Validate file type
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        alert(`File type not allowed: ${file.name}. Please upload images, PDFs, or documents.`);
        continue;
      }
      
      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        alert(`File too large: ${file.name}. Maximum size is 5MB.`);
        continue;
      }
      
      // Create preview for images
      let preview = null;
      if (file.type.startsWith('image/')) {
        preview = URL.createObjectURL(file);
      }
      
      validFiles.push({ file, preview, uploading: false, error: null });
    }
    
    setAttachments(prev => [...prev, ...validFiles]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => {
      const newAttachments = [...prev];
      // Revoke object URL if it exists
      if (newAttachments[index].preview) {
        URL.revokeObjectURL(newAttachments[index].preview);
      }
      newAttachments.splice(index, 1);
      return newAttachments;
    });
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging false if leaving the drop zone entirely
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      processFiles(files);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedType) {
      return;
    }
    
    // Validate required fields
    if (!formData.title.trim() || !formData.description.trim()) {
      return;
    }
    
    // Validate email if required
    if (form.fields.email?.required && !formData.email.trim()) {
      return;
    }
    
    setSubmitting(true);
    try {
      // First, upload any attachments via presigned S3 URLs
      const uploadedFiles = [];
      const { publicPresignedUpload } = await import('../services/upload');
      for (const attachment of attachments) {
        try {
          const result = await publicPresignedUpload(attachment.file, shareToken);
          uploadedFiles.push({
            file_id: result.file_id,
            filename: result.filename,
            content_type: result.content_type,
            size: result.size,
          });
        } catch (uploadErr) {
          console.error('Error uploading file:', uploadErr);
        }
      }
      
      const submitData = {
        type: selectedType,
        title: formData.title.trim(),
        description: formData.description.trim(),
        email: formData.email.trim() || null,
        name: formData.name.trim() || null,
        company: formData.company.trim() || null,
        phone: formData.phone.trim() || null,
        honeypot: honeypot, // Anti-spam
        url_params: Object.fromEntries(searchParams),
        attachments: uploadedFiles,
      };
      
      const response = await supportFormsApi.submitPublicForm(shareToken, submitData);
      setSubmitted(true);
      setRequiresVerification(response.requires_verification);
    } catch (err) {
      console.error('Error submitting form:', err);
      // Don't show error for spam blocks
    } finally {
      setSubmitting(false);
    }
  };

  // Get branding colors with fallbacks
  const branding = form?.branding || {};
  const colors = {
    pageBackground: branding.page_background || '#F9FAFB',
    cardBackground: branding.card_background || '#FFFFFF',
    heading: branding.heading_color || '#111827',
    body: branding.body_color || '#374151',
    muted: branding.muted_color || '#9CA3AF',
    buttonBg: branding.button_background || '#10B981',
    buttonText: branding.button_text || '#FFFFFF',
    buttonHover: branding.button_hover || '#059669',
    inputBg: branding.input_background || '#FFFFFF',
    inputBorder: branding.input_border || '#D1D5DB',
    inputFocus: branding.input_focus_border || '#10B981',
    typeCardBorder: branding.type_card_border || '#E5E7EB',
    typeCardSelected: branding.type_card_selected || '#D1FAE5',
  };

  if (loading) {
    return (
      <div className={cn(
        "min-h-screen flex items-center justify-center",
        isEmbed ? "bg-transparent" : "bg-gray-50 dark:bg-gray-900"
      )}>
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !form) {
    return (
      <div className={cn(
        "min-h-screen flex items-center justify-center",
        isEmbed ? "bg-transparent" : "bg-gray-50 dark:bg-gray-900"
      )}>
        <Card className="p-8 text-center max-w-md">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <h2 className="text-xl font-semibold mb-2">Form Not Available</h2>
          <p className="text-gray-500">{error || 'This form may have been disabled or deleted.'}</p>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: isEmbed ? 'transparent' : colors.pageBackground }}
      >
        <Card className="p-8 text-center max-w-md" style={{ backgroundColor: colors.cardBackground }}>
          <div 
            className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
            style={{ backgroundColor: `${colors.buttonBg}20` }}
          >
            <CheckCircle className="w-8 h-8" style={{ color: colors.buttonBg }} />
          </div>
          <h2 className="text-2xl font-semibold mb-2" style={{ color: colors.heading }}>Thank You!</h2>
          <p style={{ color: colors.muted }} className="mb-4">
            {requiresVerification 
              ? 'Please check your email to verify your submission.'
              : 'Your feedback has been submitted successfully.'
            }
          </p>
          {requiresVerification && (
            <p className="text-sm" style={{ color: colors.muted }}>
              Click the link in your email to complete your submission.
            </p>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen p-4 md:p-8"
      style={{ backgroundColor: isEmbed ? 'transparent' : colors.pageBackground }}
    >
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          {form.branding?.logo_url && (
            <img 
              src={form.branding.logo_url} 
              alt={form.branding.company_name || 'Logo'}
              className="h-12 mx-auto mb-4"
            />
          )}
          <h1 className="text-2xl font-bold mb-2" style={{ color: colors.heading }}>
            {form.name}
          </h1>
          {form.description && (
            <p style={{ color: colors.body }}>
              {form.description}
            </p>
          )}
          {form.branding?.company_name && !form.branding?.logo_url && (
            <p className="text-sm mt-2" style={{ color: colors.muted }}>
              Powered by {form.branding.company_name}
            </p>
          )}
        </div>

        <Card className="p-6 md:p-8" style={{ backgroundColor: colors.cardBackground }}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Type Selection */}
            {form.enabled_types.length > 1 && (
              <div>
                <Label style={{ color: colors.body }} className="mb-3 block">
                  What would you like to do? *
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {form.enabled_types.map((type) => {
                    const config = TYPE_CONFIG[type];
                    if (!config) return null;
                    const TypeIcon = config.icon;
                    const isSelected = selectedType === type;
                    
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setSelectedType(type)}
                        style={{
                          borderColor: isSelected ? colors.buttonBg : colors.typeCardBorder,
                          backgroundColor: isSelected ? colors.typeCardSelected : 'transparent',
                        }}
                        className="p-4 rounded-lg border-2 text-left transition-all hover:border-gray-400"
                      >
                        <TypeIcon 
                          className="w-6 h-6 mb-2"
                          style={{ color: isSelected ? colors.buttonBg : colors.muted }} 
                        />
                        <p className="font-medium" style={{ color: colors.heading }}>
                          {config.label}
                        </p>
                        <p className="text-xs mt-1" style={{ color: colors.muted }}>
                          {config.description}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Title */}
            <div>
              <Label htmlFor="title" style={{ color: colors.body }}>
                Title *
              </Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={selectedType === 'bug' ? "What's the issue?" : selectedType === 'feature' ? "What feature would you like?" : "What's your question?"}
                required
                className="mt-1.5"
                style={{
                  backgroundColor: colors.inputBg,
                  borderColor: colors.inputBorder,
                  color: colors.heading,
                }}
              />
            </div>

            {/* Description - WYSIWYG Editor */}
            <div>
              <Label htmlFor="description" style={{ color: colors.body }}>
                Description *
              </Label>
              <div className="mt-1.5">
                <MarkdownEditor
                  content={formData.description}
                  onChange={(value) => setFormData({ ...formData, description: value })}
                  placeholder="Please provide as much detail as possible..."
                  minHeight={150}
                />
              </div>
            </div>

            {/* Attachments - Only shown if enabled */}
            {form.enable_attachments !== false && (
              <div>
                <Label style={{ color: colors.body }} className="mb-2 block">
                  <Paperclip className="w-4 h-4 inline mr-1" />
                  Attachments (optional)
                </Label>
                <p className="text-xs mb-3" style={{ color: colors.muted }}>
                  Upload images, PDFs, or documents (max 5 files, 5MB each)
                </p>
                
                {/* File list */}
                {attachments.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {attachments.map((attachment, index) => (
                      <div 
                        key={index}
                        className="flex items-center gap-3 p-2 rounded-lg border"
                        style={{ borderColor: colors.inputBorder, backgroundColor: colors.inputBg }}
                      >
                        {attachment.preview ? (
                          <img 
                            src={attachment.preview} 
                            alt="Preview" 
                            className="w-10 h-10 object-cover rounded"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate" style={{ color: colors.heading }}>
                            {attachment.file.name}
                          </p>
                          <p className="text-xs" style={{ color: colors.muted }}>
                            {(attachment.file.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                        >
                          <X className="w-4 h-4" style={{ color: colors.muted }} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Drop zone */}
                {attachments.length < MAX_FILES && (
                  <div
                    ref={dropZoneRef}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={cn(
                      "w-full py-8 px-4 border-2 border-dashed rounded-lg cursor-pointer transition-all",
                      "flex flex-col items-center justify-center gap-2",
                      isDragging 
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" 
                        : "hover:border-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    )}
                    style={{ 
                      borderColor: isDragging ? '#3B82F6' : colors.inputBorder,
                    }}
                    data-testid="file-drop-zone"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept={ALLOWED_FILE_TYPES.join(',')}
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <Upload 
                      className={cn("w-8 h-8", isDragging ? "text-blue-500" : "")} 
                      style={{ color: isDragging ? undefined : colors.muted }} 
                    />
                    <span className="text-sm font-medium" style={{ color: isDragging ? '#3B82F6' : colors.body }}>
                      {isDragging ? 'Drop files here' : 'Click to browse or drag & drop files'}
                    </span>
                    <span className="text-xs" style={{ color: colors.muted }}>
                      Images, PDFs, Word documents
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Dynamic Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {form.fields.email?.enabled && (
                <div>
                  <Label htmlFor="email" className="text-gray-700 dark:text-gray-300">
                    Email {form.fields.email.required && '*'}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@example.com"
                    required={form.fields.email.required}
                    className="mt-1.5"
                  />
                </div>
              )}

              {form.fields.name?.enabled && (
                <div>
                  <Label htmlFor="name" className="text-gray-700 dark:text-gray-300">
                    Name {form.fields.name.required && '*'}
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your name"
                    required={form.fields.name.required}
                    className="mt-1.5"
                  />
                </div>
              )}

              {form.fields.company?.enabled && (
                <div>
                  <Label htmlFor="company" className="text-gray-700 dark:text-gray-300">
                    Company {form.fields.company.required && '*'}
                  </Label>
                  <Input
                    id="company"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Company name"
                    required={form.fields.company.required}
                    className="mt-1.5"
                  />
                </div>
              )}

              {form.fields.phone?.enabled && (
                <div>
                  <Label htmlFor="phone" className="text-gray-700 dark:text-gray-300">
                    Phone {form.fields.phone.required && '*'}
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1 (555) 000-0000"
                    required={form.fields.phone.required}
                    className="mt-1.5"
                  />
                </div>
              )}
            </div>

            {/* Honeypot (hidden anti-spam) */}
            <div className="sr-only" aria-hidden="true">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={submitting || !selectedType}
              className="w-full"
              style={{ 
                backgroundColor: colors.buttonBg, 
                color: colors.buttonText 
              }}
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {form.require_email_verification ? 'Submit & Verify Email' : 'Submit Feedback'}
            </Button>

            {/* Footer */}
            <p className="text-xs text-center text-gray-400">
              {form.branding?.company_name && (
                <>Powered by {form.branding.company_name} • </>
              )}
              <a 
                href="https://stackranked.ai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:underline"
              >
                StackRanked
              </a>
            </p>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default SupportFormPublic;
